import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Check, ChevronDown, ChevronRight, Clock, Copy, Globe, Loader2, RefreshCw, Search, Sparkles, Store as StoreIcon, Ticket } from "lucide-react";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import useGoerOneAuth from "../../hooks/useGoerOneAuth";
import WebHeader from "./WebHeader";
import { formatINR } from "./VoucherCheckoutPanel";
import { fetchMyVouchers } from "../../store/voucherSlices/myVouchersSlice";

const CONFIRMATION_PATH = "/web/payment-confirmation";

// redemption_type: 1 = online, 2 = offline, 3 = both
const REDEEM_INFO = {
    1: { label: "Online only", icons: ["online"] },
    2: { label: "In-store only", icons: ["offline"] },
    3: { label: "Online & in-store", icons: ["online", "offline"] },
};

const TABS = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "used", label: "Used" },
    { key: "expired", label: "Expired" },
];

const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const getVoucherState = (v) => {
    if (v.status === "used" || v.used_at) return "used";
    if (v.status === "expired" || (v.valid_to && new Date(v.valid_to) < new Date())) return "expired";
    return "active";
};

const getDaysLeft = (v) => {
    if (!v.valid_to) return null;
    return Math.ceil((new Date(v.valid_to) - new Date()) / 86400000);
};

// The confirmation page needs the order number; ask the backend to include it on each item
const getOrderRef = (v) => v.order_no || v.voucher_order?.order_no || v.order?.order_no || null;

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    const onCopy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard not available */
        }
    };
    return (
        <button
            type="button"
            onClick={onCopy}
            className="flex shrink-0 cursor-pointer items-center gap-1 rounded-lg bg-[#0B67FC]/10 px-2.5 py-1.5 text-xs font-semibold text-[#0B67FC] transition hover:bg-[#0B67FC]/20"
        >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
        </button>
    );
}

function VoucherCard({ v, onOpen }) {
    const [showTerms, setShowTerms] = useState(false);
    const state = getVoucherState(v);
    const live = state === "active";
    const isPercent = Number(v.discount_type) === 2;
    const customerShare = Number(v.voucher?.customer_share) || 0;
    const storeName = v.voucher?.store?.store_name || "Store not available";
    const value = Number(v.discount_value) || 0;
    const cap = Number(v.max_discount_value) || 0;
    const redeem = REDEEM_INFO[Number(v.voucher?.redemption_type)] || null;
    const daysLeft = live ? getDaysLeft(v) : null;
    const clickable = !!getOrderRef(v);
    const badge = {
        active: { text: "Active", cls: "bg-emerald-100 text-emerald-700" },
        used: { text: "Used", cls: "bg-slate-100 text-slate-600" },
        expired: { text: "Expired", cls: "bg-rose-100 text-rose-700" },
    }[state];

    // Fixed discount uses discount_value.
    // Percentage discount uses max_discount_value as specified.
    const actualDiscount = isPercent ? cap : value;
    const customerDiscount = (actualDiscount * customerShare) / 100;

    const validity =
        state === "used" && v.used_at
            ? `Used on ${formatDate(v.used_at)}`
            : v.valid_to
            ? `${state === "expired" ? "Expired on" : "Valid till"} ${formatDate(v.valid_to)}`
            : v.valid_from
            ? `Valid from ${formatDate(v.valid_from)}`
            : "No expiry date";

    return (
        <div
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={() => clickable && onOpen(v)}
            onKeyDown={(e) => clickable && e.key === "Enter" && onOpen(v)}
            className={`group relative overflow-hidden rounded-2xl border bg-white shadow-lg transition ${
                live ? "border-[#0B67FC]/20 shadow-[#0B67FC]/10" : "border-slate-200 opacity-75 shadow-slate-200/60"
            } ${clickable ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-xl" : ""}`}
        >
            <div className="flex">
                {/* stub */}
                <div
                    className={`relative flex w-24 shrink-0 flex-col items-center justify-center px-2 py-5 text-center text-white sm:w-28 ${
                        live ? "bg-gradient-to-br from-[#0B67FC] to-[#062E6C]" : "bg-gradient-to-br from-slate-400 to-slate-500"
                    }`}
                >
                    <span className="absolute -right-8 -top-8 h-20 w-20 rounded-full bg-white/10" />
                    <Ticket className="relative mb-1 h-5 w-5 text-white/70" />
                    <p className="relative text-xl font-extrabold leading-none sm:text-2xl">{isPercent ? `${value}%` : formatINR(customerDiscount)}</p>
                    <p className="relative mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/80">Off</p>
                    {isPercent && cap > 0 && <p className="relative mt-1 text-[10px] text-white/70">up to {formatINR(cap)}</p>}
                </div>

                {/* body */}
                <div className="relative min-w-0 flex-1 border-l-2 border-dashed border-slate-200 px-4 py-4">
                    <span className="absolute -left-2.5 -top-2.5 h-5 w-5 rounded-full bg-[#F3F7FF]" />
                    <span className="absolute -bottom-2.5 -left-2.5 h-5 w-5 rounded-full bg-[#F3F7FF]" />

                    
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-900">
                            {v.voucher_name}
                        </p>
                        <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-slate-600">
                            <StoreIcon className="h-3.5 w-3.5 shrink-0 text-[#0B67FC]" />
                            <span className="truncate">{storeName}</span>
                        </p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${badge.cls}`}>
                        {badge.text}
                    </span>
                </div>


                    <div className="mt-3 flex items-center justify-between gap-2 rounded-xl border border-dashed border-[#0B67FC]/30 bg-[#0B67FC]/5 px-3 py-2">
                        <div className="min-w-0">
                            <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">Voucher code</p>
                            <p className={`truncate font-mono text-sm font-bold tracking-widest sm:text-base ${live ? "text-[#062E6C]" : "text-slate-500 line-through"}`}>
                                {v.voucher_code}
                            </p>
                        </div>
                        {live && <CopyButton text={v.voucher_code} />}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        {redeem && (
                            <span className="flex items-center gap-1 font-semibold text-slate-700">
                                {redeem.icons.includes("online") && <Globe className="h-3.5 w-3.5 text-sky-600" />}
                                {redeem.icons.includes("offline") && <StoreIcon className="h-3.5 w-3.5 text-amber-600" />}
                                {redeem.label}
                            </span>
                        )}
                        <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {validity}
                        </span>
                    </div>

                    {daysLeft != null && daysLeft <= 7 && daysLeft >= 0 && (
                        <p className="mt-2 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                            {daysLeft === 0 ? "Expires today" : `Expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
                        </p>
                    )}

                    <div className="mt-2 flex items-center justify-between">
                        {v.terms_condition ? (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowTerms((s) => !s);
                                }}
                                className="flex cursor-pointer items-center gap-1 text-xs font-semibold text-[#0B67FC]"
                            >
                                Terms &amp; conditions
                                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showTerms ? "rotate-180" : ""}`} />
                            </button>
                        ) : (
                            <span />
                        )}
                        {clickable && (
                            <span className="flex items-center text-xs font-semibold text-slate-400 transition group-hover:text-[#0B67FC]">
                                Order <ChevronRight className="h-4 w-4" />
                            </span>
                        )}
                    </div>
                    {showTerms && v.terms_condition && <p className="mt-1 whitespace-pre-line text-xs text-slate-500">{v.terms_condition}</p>}
                </div>
            </div>
        </div>
    );
}

export default function MyVouchers() {
    useDocumentTitle("My Vouchers");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useGoerOneAuth();
    const { items, page, lastPage, total, loading, loadingMore, loaded, error } = useSelector((s) => s.myVouchers);

    const [tab, setTab] = useState("all");
    const [query, setQuery] = useState("");

    useEffect(() => {
        if (user?.id) dispatch(fetchMyVouchers({ page: 1 }));
    }, [dispatch, user?.id]);

    const counts = useMemo(() => {
        const c = { all: items.length, active: 0, used: 0, expired: 0 };
        items.forEach((v) => {
            c[getVoucherState(v)] += 1;
        });
        return c;
    }, [items]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items.filter(
            (v) =>
                (tab === "all" || getVoucherState(v) === tab) &&
                (!q || v.voucher_name?.toLowerCase().includes(q) || v.voucher_code?.toLowerCase().includes(q))
        );
    }, [items, tab, query]);

    const openOrder = (v) => {
        const ref = getOrderRef(v);
        if (!ref) return;
        navigate(`${CONFIRMATION_PATH}?ref=${encodeURIComponent(ref)}`, {
            state: { purchaseType: "voucher", referenceNumber: ref, storeId: v.voucher?.store_id },
        });
    };

    const showSkeleton = loading || (!loaded && !error);

    return (
        <div className="min-h-screen bg-[#F3F7FF]">
            <WebHeader name={user?.name || user?.customer_name || ""} />

            {/* Hero */}
            <div className="bg-gradient-to-br from-[#0B67FC] to-[#062E6C] px-4 pb-20 pt-10 text-white">
                <div className="mx-auto max-w-6xl">
                    <div className="flex items-center gap-3">
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
                            <Ticket className="h-6 w-6" />
                        </span>
                        <div>
                            <h1 className="text-2xl font-extrabold sm:text-3xl">My Vouchers</h1>
                            <p className="text-sm text-white/75">Every voucher you've bought, in one place</p>
                        </div>
                    </div>

                    <div className="mt-6 grid max-w-md grid-cols-3 gap-3">
                        {[
                            { label: "Total", value: loaded ? total : "-" },
                            { label: "Active", value: loaded ? counts.active : "-" },
                            { label: "Used", value: loaded ? counts.used : "-" },
                        ].map((s) => (
                            <div key={s.label} className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur">
                                <p className="text-2xl font-extrabold">{s.value}</p>
                                <p className="text-xs font-medium uppercase tracking-wider text-white/70">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mx-auto -mt-12 max-w-6xl px-4 pb-16">
                {/* Search + tabs */}
                <div className="rounded-2xl bg-white p-3 shadow-lg shadow-slate-200/70">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by voucher name or code"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-[#0B67FC] focus:bg-white focus:ring-2 focus:ring-[#0B67FC]/20"
                        />
                    </div>
                    <div className="mt-3 flex gap-2 overflow-x-auto">
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                className={`flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                                    tab === t.key ? "bg-[#0B67FC] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                }`}
                            >
                                {t.label}
                                <span className={`rounded-full px-1.5 text-xs ${tab === t.key ? "bg-white/25" : "bg-white"}`}>{counts[t.key]}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* States */}
                {showSkeleton && (
                    <div className="mt-6 grid animate-pulse gap-4 md:grid-cols-2">
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="h-48 rounded-2xl bg-slate-200/70" />
                        ))}
                    </div>
                )}

                {!showSkeleton && error && items.length === 0 && (
                    <div className="mt-6 flex flex-col items-center rounded-2xl bg-white px-6 py-12 text-center shadow">
                        <AlertCircle className="h-10 w-10 text-rose-500" />
                        <p className="mt-3 font-semibold text-slate-800">Couldn't load your vouchers</p>
                        <p className="mt-1 text-sm text-slate-500">{error}</p>
                        <button
                            type="button"
                            onClick={() => dispatch(fetchMyVouchers({ page: 1 }))}
                            className="mt-4 flex cursor-pointer items-center gap-2 rounded-xl bg-[#0B67FC] px-5 py-2.5 text-sm font-semibold text-white"
                        >
                            <RefreshCw className="h-4 w-4" /> Try again
                        </button>
                    </div>
                )}

                {!showSkeleton && !error && loaded && items.length === 0 && (
                    <div className="mt-6 flex flex-col items-center rounded-2xl bg-white px-6 py-14 text-center shadow">
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0B67FC]/10">
                            <Sparkles className="h-8 w-8 text-[#0B67FC]" />
                        </span>
                        <p className="mt-4 text-lg font-bold text-slate-800">No vouchers yet</p>
                        <p className="mt-1 max-w-xs text-sm text-slate-500">Buy a voucher at your favourite store and it will show up here with its code.</p>
                        <Link to="/web/home" className="mt-5 rounded-xl bg-[#0B67FC] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[#0B67FC]/30">
                            Explore stores
                        </Link>
                    </div>
                )}

                {!showSkeleton && items.length > 0 && filtered.length === 0 && (
                    <p className="mt-10 text-center text-sm text-slate-500">No vouchers match your search or filter.</p>
                )}

                {filtered.length > 0 && (
                    <div className="mt-6 grid gap-4 md:grid-cols-2">
                        {filtered.map((v) => (
                            <VoucherCard key={v.id} v={v} onOpen={openOrder} />
                        ))}
                    </div>
                )}

                {page < lastPage && (
                    <div className="mt-8 flex justify-center">
                        <button
                            type="button"
                            onClick={() => dispatch(fetchMyVouchers({ page: page + 1 }))}
                            disabled={loadingMore}
                            className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
                        >
                            {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                            {loadingMore ? "Loading..." : "Load more"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}